'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { Faders } from 'phosphor-icons';
import { useState } from 'react';
import { c } from 'ui-components';
import { FiltersDrawer } from '../FiltersDrawer';
import { PriceFacet } from '../PriceFacet';
import { PublisherFacet } from '../PublisherFacet';
import { RatingFacet } from '../RatingFacet';
import { ReleaseDateFacet } from '../ReleaseDateFacet';
import { ResultsPerPage } from '../ResultsPerPage';
import { HorizontalFiltersSkeleton } from '../Skeleton/HorizontalFiltersSkeleton';
import { Sort } from '../Sort';
import { UnityVersionFacet } from '../UnityVersionFacet';
import { useSearchLoading } from '../hooks/useSearchLoading';
import { HorizontalFiltersProps } from './HorizontalFilters.types';

export const HorizontalFilters = ({ className }: HorizontalFiltersProps) => {
  const { isInitialLoad } = useSearchLoading();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const filtersLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.filters.label')
      ?.value ?? 'Filters';

  const moreFiltersLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.filters.more')
      ?.value ?? 'Filters';

  if (isInitialLoad) {
    return <HorizontalFiltersSkeleton className={className} />;
  }

  return (
    <div
      role="group"
      aria-label={filtersLabel}
      className={c('items-end gap-3', className)}
    >
      {/* Filter pills */}
      <div className="flex items-center gap-2">
        <PriceFacet />
        <RatingFacet />
        <UnityVersionFacet />
        <PublisherFacet className="hidden xl:block" />
        <ReleaseDateFacet className="hidden xl:block" />
        <button
          type="button"
          onClick={() => setIsDrawerOpen(true)}
          aria-haspopup="dialog"
          className={c(
            'flex items-center gap-x-2 rounded-lg border p-2 text-sm font-normal xl:hidden',
            'focus-outline',
            'border-grey-400 bg-transparent text-[#141414]'
          )}
        >
          <Faders size={14} />
          {moreFiltersLabel}
        </button>
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Sort by + Results per page (keep their own labels) */}
      <Sort />
      <ResultsPerPage />

      <FiltersDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
      />
    </div>
  );
};
